import { useContext } from "react";
import { useParams } from "react-router";
import { ClipLoader } from "react-spinners";
import { Constants } from "../../../../../../common/constants";
import { SyncDirection } from "../../../../../../common/sesh_types";
import { SyncEntity } from "../../../../../../src/entity/Sync";
import { trpc } from "../../../utils/trpc";
import { GuildSettingsContext } from "../../guild_settings";
import LinkAccount from "../link_account/link_account";
import { SyncBox } from "./sync_box";

export default function SyncOverview(props: {
	onSetupNewSyncClicked: () => void;
	onEditClicked: (sync: SyncEntity) => void;
}) {
	const { server_id } = useParams<{ server_id: string }>();

	const { is_premium } = useContext(GuildSettingsContext);

	const getSyncsOrAuthURLQuery = trpc.gcal.getSyncsOrAuthURL.useQuery({
		guild_id: server_id,
	});

	if (getSyncsOrAuthURLQuery.isLoading) {
		return (
			<div className="flex justify-center">
				<ClipLoader
					size={"2rem"}
					loading={true}
					color={Constants.EMBED_COLOR}
				/>
			</div>
		);
	}

	if (!getSyncsOrAuthURLQuery.isSuccess) {
		return (
			<p className="has-text-danger">Error communicating with server</p>
		);
	}

	const isLoggedIn = getSyncsOrAuthURLQuery.data.isLoggedIn;

	if (!isLoggedIn) {
		return (
			<LinkAccount
				authUrl={getSyncsOrAuthURLQuery.data.authUrl}
				errorMessage={getSyncsOrAuthURLQuery.data.message}
				missingScopes={getSyncsOrAuthURLQuery.data.missingScopes}
			/>
		);
	}

	const syncs = getSyncsOrAuthURLQuery.data.syncs;

	const inbound_sync_boxes: JSX.Element[] = [];
	const outbound_sync_boxes: JSX.Element[] = [];

	syncs.forEach((sync) => {
		const sync_box = (
			<SyncBox
				key={sync.id}
				sync={sync}
				onEditClicked={props.onEditClicked}
				guild_id={server_id}
			/>
		);

		switch (sync.sync_direction) {
			case SyncDirection.Inbound:
				inbound_sync_boxes.push(sync_box);
				break;
			case SyncDirection.Outbound:
				outbound_sync_boxes.push(sync_box);
				break;
		}
	});

	return (
		<div className="flex flex-col gap-4">
			{outbound_sync_boxes.length > 0 && (
				<div className="flex flex-col gap-2">
					<div className="has-text-weight-bold is-size-5">
						Outbound Syncs ({outbound_sync_boxes.length}/
						{
							Constants.SYNC_LIMITS[SyncDirection.Outbound][
								is_premium ? "premium" : "standard"
							]
						}
						)
					</div>
					{outbound_sync_boxes}
				</div>
			)}
			{inbound_sync_boxes.length > 0 && (
				<div className="flex flex-col gap-2">
					<div className="has-text-weight-bold is-size-5">
						Inbound Syncs ({inbound_sync_boxes.length}/
						{
							Constants.SYNC_LIMITS[SyncDirection.Inbound][
								is_premium ? "premium" : "standard"
							]
						}
						)
					</div>
					{inbound_sync_boxes}
				</div>
			)}
			<div className="column is-5 p-0 ">
				<button
					className="button dashboard-primary-button gap-2"
					onClick={() => {
						props.onSetupNewSyncClicked();
					}}
				>
					<span>Setup a new sync</span>
					<div className="continue-chevron ml-auto self-center">
						<i className="fas fa-chevron-right "></i>
					</div>
				</button>
			</div>
		</div>
	);
}
